import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Bell, 
  Package, 
  AlertTriangle, 
  MessageSquare, 
  DollarSign,
  CheckCircle,
  Clock,
  Check,
  Trash2,
  RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const NotificationsPage = () => {
  const navigate = useNavigate();
  const { user, token, API } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, unread, read

  useEffect(() => {
    fetchNotifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(response.data);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      toast.error('Erreur lors du chargement des notifications');
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await axios.post(`${API}/notifications/${notificationId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
      );
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      const unreadNotifications = notifications.filter(n => !n.read);
      await Promise.all(
        unreadNotifications.map(n => 
          axios.post(`${API}/notifications/${n.id}/read`, {}, {
            headers: { Authorization: `Bearer ${token}` }
          })
        )
      );
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      toast.success('Toutes les notifications ont été marquées comme lues');
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleNotificationClick = (notification) => {
    markAsRead(notification.id);
    const data = notification.data || {};
    const isAdmin = user?.is_admin;
    switch (notification.type) {
      case 'order_pending_validation':
        navigate('/admin?tab=pending-validation'); break;
      case 'order_validated':
        navigate(data.order_id ? `/buyer?tab=orders&order=${data.order_id}` : '/buyer?tab=orders'); break;
      case 'order_rejected':
      case 'order_cancelled':
      case 'order_expired':
        navigate(data.order_id ? `/buyer?tab=cancelled&order=${data.order_id}` : '/buyer?tab=cancelled'); break;
      case 'order_status':
      case 'order_confirmed':
      case 'order_created':
      case 'order_completed':
        navigate(data.order_id ? `/buyer?tab=orders&order=${data.order_id}` : '/buyer?tab=orders'); break;
      case 'payment_confirmed':
        navigate(data.order_id ? `/buyer?tab=orders&order=${data.order_id}` : '/buyer?tab=orders'); break;
      case 'payment_released':
      case 'payment_received':
        navigate(data.order_id ? `/seller?tab=orders&order=${data.order_id}` : '/seller?tab=orders'); break;
      case 'payment_failed':
        navigate(data.order_id ? `/buyer?tab=orders&order=${data.order_id}` : '/buyer?tab=orders'); break;
      case 'new_order':
        navigate(data.order_id ? `/seller?tab=orders&order=${data.order_id}` : '/seller?tab=orders'); break;
      case 'dispute_opened':
      case 'dispute_message':
        if (data.dispute_id && isAdmin) { navigate(`/admin?tab=disputes&dispute=${data.dispute_id}`); }
        else { navigate(data.order_id ? `/buyer?tab=orders&order=${data.order_id}` : '/buyer?tab=orders'); }
        break;
      case 'dispute_resolved':
        navigate(data.order_id ? `/buyer?tab=history&order=${data.order_id}` : '/buyer?tab=history'); break;
      case 'new_support_ticket':
      case 'support_reply':
        if (data.ticket_id && isAdmin) { navigate(`/admin?tab=support&ticket=${data.ticket_id}`); }
        else { navigate(data.ticket_id ? `/messages?ticket=${data.ticket_id}` : '/messages'); }
        break;
      case 'ticket_closed':
        navigate(data.ticket_id ? `/messages?ticket=${data.ticket_id}` : '/messages'); break;
      case 'listing_approved':
      case 'listing_rejected':
        navigate(data.listing_id ? `/listings/${data.listing_id}` : '/seller?tab=flights'); break;
      case 'listing_modified':
        navigate(data.listing_id ? `/listings/${data.listing_id}` : '/buyer?tab=orders'); break;
      case 'modification_accepted':
      case 'modification_rejected':
      case 'replacement_requested':
        navigate(data.order_id ? `/seller?tab=orders&order=${data.order_id}` : '/seller?tab=orders'); break;
      case 'refund_requested':
      case 'refund_processed':
        if (isAdmin) { navigate('/admin?tab=disputes'); }
        else { navigate(data.order_id ? `/buyer?tab=orders&order=${data.order_id}` : '/buyer?tab=orders'); }
        break;
      default:
        if (data.order_id) navigate(`/buyer?tab=orders&order=${data.order_id}`);
        else if (data.listing_id) navigate(`/listings/${data.listing_id}`);
        else if (data.ticket_id) navigate(`/messages?ticket=${data.ticket_id}`);
        else navigate('/seller');
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'order_status':
      case 'order_confirmed':
      case 'order_completed':
      case 'order_created':
      case 'order_validated':
      case 'new_order':
        return <Package className="h-5 w-5 text-primary" />;
      case 'order_pending_validation':
        return <Clock className="h-5 w-5 text-amber-500" />;
      case 'order_cancelled':
      case 'order_expired':
      case 'order_rejected':
      case 'payment_failed':
        return <AlertTriangle className="h-5 w-5 text-red-500" />;
      case 'payment_released':
      case 'payment_received':
      case 'payment_confirmed':
        return <DollarSign className="h-5 w-5 text-green-500" />;
      case 'dispute_opened':
      case 'dispute_message':
      case 'dispute_resolved':
        return <AlertTriangle className="h-5 w-5 text-yellow-500" />;
      case 'listing_approved':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'listing_rejected':
        return <AlertTriangle className="h-5 w-5 text-red-500" />;
      case 'listing_modified':
        return <Package className="h-5 w-5 text-amber-500" />;
      case 'new_support_ticket':
      case 'support_reply':
      case 'ticket_closed':
        return <MessageSquare className="h-5 w-5 text-violet-500" />;
      case 'refund_requested':
      case 'refund_processed':
        return <DollarSign className="h-5 w-5 text-amber-500" />;
      default:
        return <Bell className="h-5 w-5 text-muted-foreground" />;
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    
    if (minutes < 1) return "À l'instant";
    if (minutes < 60) return `Il y a ${minutes} min`;
    if (hours < 24) return `Il y a ${hours}h`;
    if (days < 7) return `Il y a ${days} jour${days > 1 ? 's' : ''}`;
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const filteredNotifications = notifications.filter(n => {
    if (filter === 'unread') return !n.read;
    if (filter === 'read') return n.read;
    return true;
  });

  const unreadCount = notifications.filter(n => !n.read).length;

  if (!user) {
    navigate('/login');
    return null;
  }

  return (
    <div className="min-h-screen bg-background" data-testid="notifications-page">
      <Navbar />
      
      <div className="max-w-4xl mx-auto px-4 py-24">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <Bell className="h-8 w-8 text-primary" />
            <div>
              <h1 className="font-heading text-3xl font-bold">Notifications</h1>
              <p className="text-muted-foreground">
                {unreadCount > 0 ? `${unreadCount} non lue${unreadCount > 1 ? 's' : ''}` : 'Toutes lues'}
              </p>
            </div>
          </div>
          
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              size="sm"
              onClick={fetchNotifications}
              data-testid="refresh-notifications"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Actualiser
            </Button>
            {unreadCount > 0 && (
              <Button 
                variant="outline" 
                size="sm"
                onClick={markAllAsRead}
                data-testid="mark-all-read"
              >
                <Check className="h-4 w-4 mr-2" />
                Tout marquer lu
              </Button>
            )}
          </div>
        </div>

        <Tabs value={filter} onValueChange={setFilter} className="space-y-4">
          <TabsList>
            <TabsTrigger value="all">
              Toutes ({notifications.length})
            </TabsTrigger>
            <TabsTrigger value="unread">
              Non lues ({unreadCount})
            </TabsTrigger>
            <TabsTrigger value="read">
              Lues ({notifications.length - unreadCount})
            </TabsTrigger>
          </TabsList>

          <TabsContent value={filter}>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
              </div>
            ) : filteredNotifications.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Bell className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                  <p className="text-muted-foreground">
                    {filter === 'unread' ? 'Aucune notification non lue' : 
                     filter === 'read' ? 'Aucune notification lue' : 
                     'Aucune notification'}
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-2">
                {filteredNotifications.map((notification) => (
                  <Card 
                    key={notification.id}
                    className={`cursor-pointer hover:border-primary/30 transition-colors ${
                      !notification.read ? 'bg-primary/5 border-primary/20' : ''
                    }`}
                    onClick={() => handleNotificationClick(notification)}
                    data-testid={`notification-${notification.id}`}
                  >
                    <CardContent className="py-4">
                      <div className="flex items-start gap-4">
                        <div className="mt-1">
                          {getNotificationIcon(notification.type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-4">
                            <div>
                              <p className={`font-medium ${!notification.read ? 'text-foreground' : 'text-muted-foreground'}`}>
                                {notification.title}
                              </p>
                              <p className="text-sm text-muted-foreground mt-1">
                                {notification.message}
                              </p>
                            </div>
                            {!notification.read && (
                              <div className="w-2 h-2 rounded-full bg-primary flex-shrink-0 mt-2" />
                            )}
                          </div>
                          <div className="flex items-center gap-4 mt-2">
                            <span className="text-xs text-muted-foreground flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {formatTime(notification.created_at)}
                            </span>
                            {notification.read && (
                              <span className="text-xs text-muted-foreground flex items-center gap-1">
                                <CheckCircle className="h-3 w-3" />
                                Lu
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default NotificationsPage;
